import { ArrowsClockwiseIcon } from "@phosphor-icons/react/ArrowsClockwise";
import { PlusIcon } from "@phosphor-icons/react/Plus";
import { Button } from "@freightsimple/ui-core/Controls/Button";
import { Heading } from "@freightsimple/ui-core/Typography/Heading";
import { Row } from "@freightsimple/ui-core/ComponentLayout/Row";
import { Stack } from "@freightsimple/ui-core/ComponentLayout/Stack";
import { T } from "@freightsimple/ui-core/Typography/T";
import FullWidthLayout from "@/Shared/Layout/CustomComponents/FullWidthLayout";
import { SavedCommoditiesTable } from "./Tables/SavedCommoditiesTable";
import { SavedCommoditiesFilterBar } from "./Bars/SavedCommoditiesFilterBar";
import { useSavedCommoditiesScreen } from "./Hooks/useSavedCommoditiesScreen";
import { useSavedCommoditiesFilters } from "./Hooks/useSavedCommoditiesFilters";

export function SavedCommoditiesScreen() {
  const {
    savedCommodities,
    loading,
    shopifyConnected,
    syncingFromShopify,
    onAdd,
    onModify,
    onDelete,
    onSyncFromShopify,
    dialogs,
  } = useSavedCommoditiesScreen();

  const filters = useSavedCommoditiesFilters(savedCommodities);

  return (
    <>
      {/* Outside the layout so the add dialog also works from the empty
       * state (the layout renders either emptyState or content, not both). */}
      {dialogs}
      <FullWidthLayout
        header="Saved Commodities"
        microcopy="Save commodity information to speed up future bookings"
        rightContent={
          <Row gap={2}>
            {shopifyConnected && (
              <Button
                icon={ArrowsClockwiseIcon}
                onClick={onSyncFromShopify}
                loading={syncingFromShopify}
              >
                Sync from Shopify
              </Button>
            )}
            <Button
              primary
              icon={PlusIcon}
              onClick={onAdd}
              data-testid="add-new-saved-commodity"
            >
              Add New Saved Commodity
            </Button>
          </Row>
        }
        loading={loading}
        showEmptyState={savedCommodities?.length === 0}
        emptyState={
          <Stack gap={6} align="center">
            <Stack gap={2} align="center">
              <Heading level={1}>You have no saved commodities</Heading>
              <T color="secondary">
                Save commodity information to speed up future bookings.
              </T>
            </Stack>
            <Button
              primary
              size="large"
              icon={PlusIcon}
              iconWeight="fill"
              onClick={onAdd}
            >
              Add New Saved Commodity
            </Button>
          </Stack>
        }
        content={
          <Stack gap={4}>
            <SavedCommoditiesFilterBar
              source={filters.source}
              setSource={filters.setSource}
              problem={filters.problem}
              setProblem={filters.setProblem}
              counts={filters.counts}
            />
            {/* A filter that matches nothing is NOT the empty state: that one says "you have no
                saved commodities", which read as data loss when the truth was a filter. Says which
                filter and how many rows it hid. */}
            {filters.filtering && filters.filtered.length === 0 ? (
              <T color="secondary">
                None of your {filters.total} saved commodities match this
                filter.
              </T>
            ) : (
              <SavedCommoditiesTable
                savedCommodities={filters.filtered}
                onModify={onModify}
                onDelete={onDelete}
              />
            )}
          </Stack>
        }
      />
    </>
  );
}
